import { KnownInboxLabel } from "@qw/qw-common";
import { Button, Icon, Separator, Spinner } from "@qw/ui";
import { useConversation } from "@/providers/use-conversation";
import {
  type ConversationMenuItem,
  type ConversationMenuItemId,
  useCoversationMenu,
} from "@/providers/use-conversation-menu";
import { useDraftsCompose } from "@/providers/use-drafts-compose";

type ButtonConversationMenuItem = Extract<ConversationMenuItem, { type: "button" }>;

const getButtonMenuItem = (menuItems: ConversationMenuItem[], id: ConversationMenuItemId) =>
  menuItems.find((item): item is ButtonConversationMenuItem => item.type === "button" && item.id === id);

export const ConversationFooter = () => {
  const { inboxConversation } = useConversation();
  const { getDraftPlacementForConversation } = useDraftsCompose();
  // Replies started from the open thread render inline, so the footer has to hand the menu the
  // placement rather than letting it fall back to floating.
  const { menuItems } = useCoversationMenu({
    conversation: inboxConversation,
    draftPlacement: getDraftPlacementForConversation(inboxConversation?.id),
  });

  // The menu reuses the `inbox` id for both Archive and "Move to inbox", so this label check is the
  // whole guard against offering to unarchive from down here — the ⋯ menu already covers that. The
  // rest is inherited: `inbox` is emitted only for the viewer's own mailbox, and nothing at all when
  // the thread is read-only.
  const isInbox = inboxConversation?.labels.includes(KnownInboxLabel.INBOX);
  const actions = [
    getButtonMenuItem(menuItems, "reply-all"),
    getButtonMenuItem(menuItems, "forward"),
    isInbox ? getButtonMenuItem(menuItems, "inbox") : undefined,
  ].filter((action) => action !== undefined);

  if (!actions.length) {
    return null;
  }

  return (
    <footer className="pl-(--gutter)">
      <Separator />
      <nav className="flex gap-3 items-center pt-6 pb-12">
        {actions.map(({ id, disabled, icon, label, onClick }) => (
          <Button key={id} variant="outline" size="sm" disabled={disabled} onClick={onClick}>
            {/* Keyed off the action's own `disabled`, not the menu's aggregate loading flag. */}
            {disabled ? <Spinner /> : <Icon icon={icon === "inventory_2" ? "archive" : icon} />}
            {label}
          </Button>
        ))}
      </nav>
    </footer>
  );
};
